import { Auth0Client } from "@auth0/auth0-spa-js";

import { logger } from "../logger";

const log = logger.getSubLogger({ name: "TokenManager" });

export class TokenError extends Error {
  constructor(
    message: string,
    public code: string
  ) {
    super(message);
    this.name = "TokenError";
  }
}

export class TokenManager {
  private static instance: TokenManager;
  private auth0Client?: Auth0Client;
  private tokenCache?: {
    token: string;
    expiresAt: number;
  };
  private refreshPromise?: Promise<string>;
  private initialized = false;
  private readyWaiters: Array<{
    resolve: () => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }> = [];

  private constructor() {
    // noop
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  initialize(auth0Client: Auth0Client) {
    if (!auth0Client) {
      log.warn("initialize called without an Auth0 client; ignoring");
      return;
    }
    this.auth0Client = auth0Client;
    this.initialized = true;
    for (const waiter of this.readyWaiters) {
      clearTimeout(waiter.timeoutId);
      waiter.resolve();
    }
    this.readyWaiters = [];
  }

  isInitialized(): boolean {
    return this.initialized && !!this.auth0Client;
  }

  /**
   * Resolves once an Auth0 client has been registered. Used by API interceptors
   * that may fire before bootstrap finishes wiring auth.
   */
  waitUntilInitialized(timeoutMs = 10000): Promise<void> {
    if (this.isInitialized()) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.readyWaiters = this.readyWaiters.filter((w) => w.timeoutId !== timeoutId);
        reject(
          new TokenError(`TokenManager not initialized within ${timeoutMs}ms`, "NOT_INITIALIZED")
        );
      }, timeoutMs);
      this.readyWaiters.push({ resolve, reject, timeoutId });
    });
  }

  private isTokenExpired(): boolean {
    if (!this.tokenCache) return true;
    // Add 60 second buffer before expiration
    return Date.now() >= this.tokenCache.expiresAt - 60000;
  }

  async getToken(): Promise<string> {
    if (!this.isInitialized()) {
      throw new TokenError("TokenManager not initialized with Auth0 client", "NOT_INITIALIZED");
    }

    try {
      // If there's an ongoing refresh, wait for it
      if (this.refreshPromise) {
        return this.refreshPromise;
      }

      // Check if we have a valid cached token
      if (this.tokenCache && !this.isTokenExpired()) {
        return this.tokenCache.token;
      }

      // Get a new token
      this.refreshPromise = this.refreshToken();
      const token = await this.refreshPromise;
      this.refreshPromise = undefined;
      return token;
    } catch (error) {
      this.refreshPromise = undefined;
      throw new TokenError(error.message || "Failed to get token", error.code || "TOKEN_ERROR");
    }
  }

  private async refreshToken(forceNetwork = false): Promise<string> {
    if (!this.isInitialized()) {
      throw new TokenError("TokenManager not initialized with Auth0 client", "NOT_INITIALIZED");
    }

    try {
      // Use Auth0's cache by default to avoid an unnecessary token refresh on
      // post-login or cold open. Only bypass it when we explicitly need a fresh token.
      log.debug(
        forceNetwork
          ? "Forcing network token refresh…"
          : "Resolving bearer token (Auth0 cache allowed)…"
      );
      const response = await this.auth0Client!.getTokenSilently({
        ...(forceNetwork ? { cacheMode: "off" as const } : {}),
        detailedResponse: true,
      });

      if (!response.access_token) {
        throw new TokenError("No token received", "NO_TOKEN");
      }

      // Cache the token with expiration
      this.tokenCache = {
        token: response.access_token,
        expiresAt: Date.now() + response.expires_in * 1000,
      };

      const expiresAt = new Date(this.tokenCache.expiresAt).toISOString();
      log.debug(`Bearer token ready. Expires at ${expiresAt}.`);

      return response.access_token;
    } catch (error) {
      // If the cache-first read fails, retry once with a network refresh.
      // Not for `login_required` (the user must authenticate again) and not for `timeout`
      if (!forceNetwork && error.error !== "login_required" && error.error !== "timeout") {
        log.debug("Cache-first token read failed; retrying with network refresh", {
          errorCode: error.error,
        });
        return this.refreshToken(true);
      }

      // Clear cache on error
      this.tokenCache = undefined;

      // The SDK has already cleared its local session on login_required. Navigation is left to
      // the caller: a navigation started here would race the caller's own redirect to login.
      if (error.error === "login_required") {
        log.error(
          "Token refresh failed: Auth0 returned login_required. " +
            "Both the refresh token and the SSO session fallback have failed.",
          { originalError: error }
        );
        throw new TokenError("Session expired", "SESSION_EXPIRED");
      }

      log.error("Token refresh failed with an unexpected error.", {
        errorCode: error.error,
        errorMessage: error.message,
      });
      throw new TokenError(
        error.message || "Failed to refresh token",
        error.error || "REFRESH_ERROR"
      );
    }
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getToken();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  clearCache() {
    this.tokenCache = undefined;
    this.refreshPromise = undefined;
  }
}

export const tokenManager = TokenManager.getInstance();
